import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button } from "reactstrap";
import {
  FaUser,
  FaTimes,
  FaShoppingCart,
  FaHeart,
  FaHistory,
  FaSun,
  FaMoon,
  FaHome,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { darkModeToggle } from "../store/actions/GlobalAction";
import {
  getSingleUser,
  logoutAction,
  updateThemeAction,
} from "../store/actions/authAction";
import { clearCartAction, clearCartDetails } from "../store/actions/cartAction";
import "./SideBar.css";

const SideBar = ({
  toggleSidebar,
  sidebarRef,
  sidebarOpen,
  setSidebarOpen,
}) => {
  const location = useLocation();
  const history = useHistory();
  const { darkMode, color } = useSelector((state) => state.global);
  let { uid } = useSelector((state) => state.authUser);
  const { cartItems } = useSelector((state) => state.cart);
  const { singleUser } = useSelector((state) => state.user);
  const { restaurantsDetail } = useSelector((state) => state.restaurants);
  const dispatch = useDispatch();
  const [isDarkMode, setIsDarkMode] = useState();

  const isNavigationSelected = (value) => {
    const currentPath = location.pathname;

    switch (value) {
      case "main":
        return currentPath === "/" || currentPath === "/main";
      case "home":
        return currentPath === "/home" || currentPath === "/restaurants";
      case "profile":
        return currentPath === "/profile";
      case "cart":
        return currentPath === "/cart";
      case "liked":
        return currentPath === "/liked";
      case "order-history":
        return currentPath === "/order-history";
      default:
        return currentPath === `/${value}`;
    }
  };

  const navigations = [
    {
      id: 1,
      name: "Login",
      icon: <FaUser className="nav-icon" />,
      value: "main",
      guestOnly: true,
      authOnly: false,
    },
    {
      id: 8,
      name: "Home",
      icon: <FaHome className="nav-icon" />,
      value: "home",
      guestOnly: false,
      authOnly: false,
    },
    {
      id: 2,
      name: "My Profile",
      icon: <FaUser className="nav-icon" />,
      value: "profile",
      guestOnly: false,
      authOnly: true,
    },
    {
      id: 3,
      name: "Cart",
      icon: <FaShoppingCart className="nav-icon" />,
      value: "cart",
      guestOnly: false,
      authOnly: false,
    },
    {
      id: 4,
      name: "Favorites",
      icon: <FaHeart className="nav-icon" />,
      value: "liked",
      guestOnly: false,
      authOnly: true,
    },
    {
      id: 7,
      name: "Order History",
      icon: <FaHistory className="nav-icon" />,
      value: "order-history",
      guestOnly: false,
      authOnly: true,
    },
    {
      id: 5,
      name: "Dark Mode",
      icon: darkMode ? (
        <FaSun className="nav-icon" />
      ) : (
        <FaMoon className="nav-icon" />
      ),
      value: "darkmode",
      guestOnly: false,
      authOnly: false,
    },
  ];

  const toggleNavigation = (value) => {
    if (value === "darkmode") {
      setIsDarkMode(!darkMode);
    } else {
      history.push(`/${value}`);
    }

    setSidebarOpen(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [history]);

  useEffect(() => {
    setIsDarkMode(singleUser.darkMode);
  }, [singleUser]);

  useEffect(() => {
    if (uid) {
      dispatch(getSingleUser(uid));
    }
  }, [uid, dispatch]);

  useEffect(() => {
    if (
      !uid &&
      (location.pathname === "/profile" ||
        location.pathname === "/order-history" ||
        location.pathname === "/payment-method")
    ) {
      history.push("/main");
    }
    if (!uid && location.pathname === "/payment") {
      localStorage.setItem("cartPath", "/cart");
      toast.warning("First you have to login");
      history.push("/main");
    }
    window.scrollTo(0, 0);
  }, [uid, location, history]);

  useEffect(() => {
    if (uid && isDarkMode !== undefined) {
      dispatch(
        updateThemeAction({
          id: uid,
          darkMode: isDarkMode,
        })
      );
    } else if (!uid && isDarkMode !== undefined) {
      dispatch(darkModeToggle(isDarkMode));
    }
  }, [dispatch, isDarkMode, uid]);

  return (
    <>
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar} />
      )}
      <div
        ref={sidebarRef}
        className={`sidebar ${sidebarOpen ? "open" : ""} ${
          darkMode ? "dark" : "light"
        }`}
      >
        <div className="sidebar-header" style={{ background: color }}>
          <div className="user-profile">
            <div className="user-avatar">
              {uid && singleUser?.imageURL ? (
                <img
                  src={singleUser.imageURL}
                  alt={singleUser.name || "User"}
                  className="avatar-img"
                />
              ) : (
                <div className="avatar-placeholder">
                  <FaUser className="avatar-icon" />
                </div>
              )}
            </div>
            <div className="user-info">
              <h4 className="user-name">
                {uid ? singleUser?.name || "Guest User" : "Welcome"}
              </h4>
              {uid && singleUser?.email && (
                <p className="user-email">{singleUser.email}</p>
              )}
            </div>
          </div>
          <button className="close-btn" onClick={toggleSidebar}>
            <FaTimes className="close-icon" />
          </button>
        </div>
        <div className="sidebar-content">
          <nav className="nav-menu">
            {navigations.map((item) => {
              if ((!uid && item.authOnly) || (uid && item.guestOnly)) {
                return null;
              }

              const isSelected = isNavigationSelected(item.value);
              const showCartBadge =
                item.name === "Cart" && cartItems?.length > 0;

              return (
                <div
                  key={item.id}
                  className={`nav-item ${isSelected ? "selected" : ""}`}
                  onClick={() => toggleNavigation(item.value)}
                >
                  <div className="nav-item-content">
                    <span className="nav-icon">
                      {item.icon || (
                        <img
                          src={darkMode ? item.src_dark : item.src}
                          alt={item.name}
                          width={20}
                        />
                      )}
                    </span>
                    <span className="nav-text">
                      {item.name === "Dark Mode" && darkMode
                        ? "Light Mode"
                        : item.name}
                    </span>
                    {showCartBadge && (
                      <span className="cart-badge">{cartItems.length}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </nav>
          <div className="sidebar-footer">
            {uid && (
              <Button
                className="logout-btn"
                style={{ background: color }}
                onClick={(e) => {
                  e.preventDefault();
                  dispatch(clearCartAction());
                  dispatch(clearCartDetails());
                  localStorage.clear();
                  dispatch(logoutAction());
                }}
              >
                <FaTimes className="mr-2" />
                Logout
              </Button>
            )}
            {restaurantsDetail?.id && (
              <Button
                className="feedback-btn"
                onClick={() => history.push("/feedback")}
              >
                <span className="mr-2">💬</span>
                Feedback
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SideBar;
